import { useId, useState, type ReactNode } from 'react';

interface Props {
  id: string;
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  /** `current-password` ao entrar ou confirmar; `new-password` ao definir uma nova. */
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
  dica?: ReactNode;
}

/**
 * Campo de senha com o olho de revelar.
 *
 * Senha digitada às cegas é senha digitada errado, e o cockpit exige 12 caracteres com
 * maiúscula, número e símbolo — combinação que ninguém acerta duas vezes seguidas sem ver.
 * O custo de errar não é só o reteclado: no primeiro acesso, a senha temporária vence em
 * três dias e convite vencido não se recupera, só se reenvia.
 *
 * Começa sempre oculto, e cada campo tem seu próprio estado: revelar a senha atual não
 * revela a nova. Quem estiver olhando por cima do ombro continua sendo problema de quem
 * digita — a decisão de mostrar é dela, e é por isso que o padrão é esconder.
 *
 * Existe como componente porque são cinco campos em três telas. Repetir o botão em cada
 * uma seria repetir também o `type="button"` — esquecê-lo faz o olho **enviar o
 * formulário**, que num campo de senha significa tentativa de login gasta a cada espiada.
 */
export function CampoDeSenha({ id, rotulo, valor, aoMudar, autoComplete, autoFocus, dica }: Props) {
  const [revelada, setRevelada] = useState(false);
  const idDaDica = useId();

  return (
    <div className="campo">
      <label htmlFor={id}>{rotulo}</label>
      <div className="campo-com-olho">
        <input
          id={id}
          type={revelada ? 'text' : 'password'}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-describedby={dica ? idDaDica : undefined}
          required
        />
        {/*
         * `type="button"` é obrigatório: dentro de um `<form>`, o padrão de um botão é
         * `submit`, e o olho enviaria o formulário a cada clique.
         *
         * `tabIndex={-1}` mantém o Tab indo do campo direto para o próximo campo, que é o
         * caminho de quem sabe a senha e só quer entrar. Quem quer o olho alcança pelo
         * ponteiro ou pelo leitor de tela, que não usa a ordem de tabulação.
         */}
        <button
          type="button"
          className="olho"
          onClick={() => setRevelada((antes) => !antes)}
          aria-controls={id}
          aria-pressed={revelada}
          aria-label={
            revelada ? `Ocultar ${rotulo.toLowerCase()}` : `Mostrar ${rotulo.toLowerCase()}`
          }
          title={revelada ? 'Ocultar' : 'Mostrar'}
          tabIndex={-1}
        >
          {revelada ? <IconeOlhoFechado /> : <IconeOlhoAberto />}
        </button>
      </div>
      {dica ? (
        <div className="dica" id={idDaDica}>
          {dica}
        </div>
      ) : null}
    </div>
  );
}

/*
 * Ícones em SVG inline, com `currentColor`: a CSP é `script-src 'self'` e não há biblioteca
 * de ícones no cockpit. `currentColor` é o que faz os dois temas funcionarem sem cor
 * literal — quem define o tom é a regra `.olho`, a partir dos tokens.
 */
const TRACO = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function IconeOlhoAberto() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M1.8 12S5.5 5.4 12 5.4 22.2 12 22.2 12 18.5 18.6 12 18.6 1.8 12 1.8 12Z"
        {...TRACO}
      />
      <circle cx="12" cy="12" r="3.2" {...TRACO} />
    </svg>
  );
}

function IconeOlhoFechado() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3.5 6.2C2.3 7.6 1.8 9 1.8 9s3.7 6.6 10.2 6.6c1.4 0 2.6-.3 3.7-.7" {...TRACO} />
      <path d="M19.2 14.4c1.9-1.4 3-3.4 3-3.4S18.5 4.4 12 4.4c-.9 0-1.7.1-2.4.3" {...TRACO} />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" {...TRACO} />
      <path d="M3 3l18 18" {...TRACO} />
    </svg>
  );
}
